import { toggleCollapsible, ToggleCollapsibleEvent } from "../Collapsible/collapsible";

export type FilterDropdownDefinition = {
  clearOptions: () => void;
  disableOption: (optionValue: string) => void;
  enableOption: (optionValue: string) => void;
  toggleOption: (optionValue: string) => void;
  updateTotalMatching: (optionValue: string, totalMatching: number) => void;
};

declare global {
  interface Window {
    ani_filterDropdowns?: Map<string, FilterDropdownDefinition>;
  }
}

if (!window.ani_filterDropdowns) {
  window.ani_filterDropdowns = new Map<string, FilterDropdownDefinition>();
}

const filterDropdowns = window.ani_filterDropdowns;

const PAGE_SIZE = 10;
const COLLAPSIBLE_TRANSITION_DURATION_MS = 100;

export const initializeFilterDropdown = (filterDropdown: HTMLElement) => {
  const id = filterDropdown.id;

  // Make sure not to double-initialize
  if (filterDropdowns.has(id) === false) {
    const baseId = id.substring("fd-".length);

    const content = filterDropdown.querySelector<HTMLElement>("label");

    const label = filterDropdown.querySelector<HTMLElement>("[data-label]");

    const select = filterDropdown.querySelector<HTMLSelectElement>("select");

    const selectOptions = select ? Array.from(select.options) : [];

    const selectedOptionsCounter = filterDropdown.querySelector<HTMLSelectElement>(
      "[data-selected-options-counter]"
    );

    const listbox = filterDropdown.querySelector<HTMLElement>('[role="listbox"]');

    const optionsNodeList = listbox?.querySelectorAll<HTMLDivElement>('[role="option"]');

    const options = optionsNodeList ? Array.from(optionsNodeList) : [];

    const collapsibleId = `fd-collapsible-${baseId}`;

    const openCollapsibleButton = filterDropdown.querySelector<HTMLSelectElement>(
      `[aria-controls="${collapsibleId}"]`
    );

    const collapsible =
      filterDropdown.querySelector<HTMLSelectElement>("[data-collapsible]");

    if (!content) {
      throw "Filter dropdown content missing";
    }
    if (!label) {
      throw "Filter dropdown label missing";
    }
    if (!select) {
      throw "Filter dropdown select missing";
    }
    if (!listbox) {
      throw "Filter dropdown listbox missing";
    }
    if (!options) {
      throw "Filter dropdown options missing";
    }
    if (!openCollapsibleButton) {
      throw "Filter dropdown openCollapsibleButton missing";
    }
    if (!collapsible) {
      throw "Filter dropdown collapsible missing";
    }

    if (navigator.userAgent.includes("Chrome")) {
      // Force "desktop mode" for Chrome mobile, since that browser seems to have removed
      // its "select multiple" native popup/overlay functionality.
      filterDropdown.classList.toggle("is-chrome");
    }

    let focusTimer: number;

    const baseLabelText = label.dataset.label;

    let focusIndex = select.options.selectedIndex;

    const updateOptionsCounter = () => {
      if (selectedOptionsCounter) {
        const numSelectedOptions = select.selectedOptions.length;

        if (numSelectedOptions === 0) {
          selectedOptionsCounter.classList.add("opacity-0");
        } else {
          selectedOptionsCounter.classList.remove("opacity-0");

          selectedOptionsCounter.innerHTML = `${numSelectedOptions}`;
        }
      }
    };

    const toggleOption = (option: HTMLDivElement) => {
      if (option.dataset.disabled === undefined) {
        let isSelected = false;

        if (select.multiple) {
          // Find the actual <select> option and toggle it
          const selectOption = selectOptions.find(
            selectOption => selectOption.value === option.dataset.value
          );

          if (selectOption) {
            selectOption.selected = !selectOption.selected;
            isSelected = selectOption.selected;
          }
        } else {
          // Select current and deselect previous option
          const previousOption = options.find(opt => opt.dataset.value === select.value);

          if (previousOption) {
            previousOption.ariaSelected = "false";
          }

          select.value = option.dataset.value ?? "";

          isSelected = true;
        }

        option.ariaSelected = isSelected ? "true" : "false";

        const event = new Event("change");

        select.dispatchEvent(event);
      }
    };

    const tryFocus = (newFocusIndex: number, delay = false) => {
      const newOption = options[newFocusIndex];

      if (newOption.dataset.disabled === undefined) {
        const previousOption = focusIndex !== -1 ? options[focusIndex] : null;

        if (previousOption) {
          previousOption.blur();
        }

        if (newOption) {
          // Focusing while the collapsible is transitioning does not look good
          if (delay) {
            focusTimer = window.setTimeout(
              () => newOption.focus(),
              COLLAPSIBLE_TRANSITION_DURATION_MS
            );
          } else {
            newOption.focus();
          }
        }

        focusIndex = newFocusIndex;

        return true;
      }

      return false;
    };

    const updateFocusedOption = (newFocusIndex: number, delay = false) => {
      const isDown = newFocusIndex > focusIndex;

      let success = false;

      // Need to loop here in case there's a bunch of disabled options in the dropdown
      // (ie disabled filter facets)
      do {
        success = tryFocus(newFocusIndex, delay);
        newFocusIndex += isDown ? 1 : -1;
      } while (!success && newFocusIndex >= 0 && newFocusIndex < options.length);
    };

    options.forEach(option => {
      option.addEventListener("click", _ => {
        toggleOption(option);
      });
    });

    const onToggleCollapsible = (isOpening: boolean, event?: Event) => {
      if (!isOpening) {
        clearTimeout(focusTimer);

        if (event instanceof KeyboardEvent && event.key !== "Tab") {
          openCollapsibleButton.focus();
        }
      }
    };

    collapsible.addEventListener("ani:toggle", e => {
      const event = e as ToggleCollapsibleEvent;
      onToggleCollapsible(event.detail.isOpening, event.detail.originalEvent);
    });

    const openCollapsible = (event: Event) => {
      toggleCollapsible(collapsible, { event, forceState: true });
    };

    const closeCollapsible = (event: Event) => {
      toggleCollapsible(collapsible, { event, forceState: false });
    };

    openCollapsibleButton.addEventListener("keydown", e => {
      const openKeys = ["ArrowDown", "ArrowUp", "Enter", " ", "Home", "End"];
      const closeKeys = ["Tab"];
      const homeKeys = ["ArrowUp", "Home"];

      if (openKeys.includes(e.key)) {
        if (homeKeys.includes(e.key)) {
          updateFocusedOption(0, true);
        } else if (e.key === "End") {
          updateFocusedOption(options.length - 1, true);
        } else {
          updateFocusedOption(focusIndex === -1 ? 0 : focusIndex, true);
        }

        openCollapsible(e);

        e.preventDefault();
      } else if (closeKeys.includes(e.key)) {
        closeCollapsible(e);
      }
    });

    listbox.addEventListener("keydown", e => {
      const key = e.key;

      const selectKeys = ["Enter", " "];
      const navKeys = ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End"];

      if (key === "Tab") {
        closeCollapsible(e);
      } else if (selectKeys.includes(key)) {
        const option = options[focusIndex];

        if (option) {
          toggleOption(option);
        }

        e.preventDefault();
      } else if (navKeys.includes(e.key)) {
        let newIndex = focusIndex;

        const max = options.length - 1;

        switch (key) {
          case "ArrowUp":
            newIndex = Math.max(0, focusIndex - 1);
            break;
          case "PageUp":
            newIndex = Math.max(0, focusIndex - PAGE_SIZE);
            break;
          case "ArrowDown":
            newIndex = Math.min(max, focusIndex + 1);
            break;
          case "PageDown":
            newIndex = Math.min(max, focusIndex + PAGE_SIZE);
            break;
          case "Home":
            newIndex = 0;
            break;
          case "End":
            newIndex = max;
            break;
        }

        updateFocusedOption(newIndex);

        e.preventDefault();
      }
    });

    select.addEventListener("change", _ => {
      if (select.multiple) {
        updateOptionsCounter();
      } else {
        const selectedOption = select.selectedOptions[0];

        if (selectedOption) {
          label.innerHTML = `${baseLabelText}: ${select.selectedOptions[0].innerText}`;
        }
      }
    });

    // Prevent form submit on enter
    select.addEventListener("keydown", e => {
      if (e.key === "Enter") {
        e.preventDefault();
      }
    });

    const definition: FilterDropdownDefinition = {
      clearOptions: () => {
        if (select.multiple) {
          for (const selectOption of selectOptions.filter(option => option.selected)) {
            const option = options.find(opt => opt.dataset.value === selectOption.value);

            if (option) {
              toggleOption(option);
            }
          }
        }
      },
      disableOption: (optionValue: string) => {
        const option = options.find(opt => opt.dataset.value === optionValue);

        const selectOption = selectOptions.find(
          selectOption => selectOption.value === optionValue
        );

        if (option && selectOption) {
          option.dataset.disabled = "";
          option.removeAttribute("tabindex");
          selectOption.disabled = true;
        }
      },
      enableOption: (optionValue: string) => {
        const option = options.find(opt => opt.dataset.value === optionValue);

        const selectOption = selectOptions.find(
          selectOption => selectOption.value === optionValue
        );

        if (option && selectOption) {
          delete option.dataset.disabled;
          option.setAttribute("tabindex", "-1");
          selectOption.disabled = false;
        }
      },
      toggleOption: (optionValue: string) => {
        const option = options.find(opt => opt.dataset.value === optionValue);

        if (option) {
          toggleOption(option);
        }
      },
      updateTotalMatching: (optionValue: string, totalMatching: number) => {
        const option = options.find(opt => opt.dataset.value === optionValue);
        const totalMatchingElement = option?.querySelector("[data-total-matching]");

        if (totalMatchingElement) {
          totalMatchingElement.innerHTML = `(${totalMatching})`;
        }
      }
    };

    filterDropdowns.set(id, definition);
  }
};

const getFilterDropdownDefinition = (element: HTMLElement) =>
  filterDropdowns.get(element.id);

export const clearFilterDropdownOptions = (element: HTMLElement) => {
  const definition = getFilterDropdownDefinition(element);

  definition?.clearOptions();
};

export const disableFilterDropdownOption = (
  element: HTMLElement,
  optionValue: string
) => {
  const definition = getFilterDropdownDefinition(element);

  definition?.disableOption(optionValue);
};

export const enableFilterDropdownOption = (element: HTMLElement, optionValue: string) => {
  const definition = getFilterDropdownDefinition(element);

  definition?.enableOption(optionValue);
};

export const updateFilterDropdownOptionTotalMatching = (
  element: HTMLElement,
  optionValue: string,
  totalMatching: number
) => {
  const definition = getFilterDropdownDefinition(element);

  definition?.updateTotalMatching(optionValue, totalMatching);
};

export const toggleFilterDropdownOption = (element: HTMLElement, optionValue: string) => {
  const definition = getFilterDropdownDefinition(element);

  definition?.toggleOption(optionValue);
};
